import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertTriangle, PackageX, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { formatMoney } from '@nokowazzi/types';
import { Button, EmptyState, SegmentedControl, Spinner } from '@nokowazzi/ui';
import { PageHeader } from '../components/PageHeader';
import { DirectionSplit } from '../features/dashboard/DirectionSplit';
import { KpiCard } from '../features/dashboard/KpiCard';
import { adminDashboardQuery } from '../features/dashboard/queries';
import type { DashboardRange, DashboardResponse } from '../features/dashboard/queries';
import { TrendChart } from '../features/dashboard/TrendChart';
import { ApiError } from '../lib/api';

export const Route = createFileRoute('/_admin/')({
  component: DashboardScreen,
});

const RANGE_OPTIONS: { value: DashboardRange; label: string }[] = [
  { value: 'today', label: '오늘' },
  { value: '7d', label: '7일' },
  { value: '30d', label: '30일' },
];

function formatCount(value: number, unit: string): string {
  return `${value.toLocaleString('ko-KR')}${unit}`;
}

function DashboardScreen() {
  const navigate = useNavigate();
  const [range, setRange] = useState<DashboardRange>('7d');
  const dashboardQuery = useQuery(adminDashboardQuery(range));

  return (
    <>
      <PageHeader
        title="대시보드"
        description="핵심 지표 요약"
        actions={
          <div className="w-64">
            <SegmentedControl
              options={RANGE_OPTIONS}
              value={range}
              onChange={(value) => setRange(value)}
            />
          </div>
        }
      />

      {dashboardQuery.isPending ? (
        <div className="flex min-h-52 items-center justify-center">
          <Spinner label="지표 불러오는 중" />
        </div>
      ) : dashboardQuery.isError ? (
        <EmptyState
          title="지표를 불러오지 못했습니다"
          description={
            dashboardQuery.error instanceof ApiError
              ? dashboardQuery.error.message
              : '잠시 후 다시 시도해 주세요'
          }
          action={
            <Button variant="tertiary" size="sm" onClick={() => void dashboardQuery.refetch()}>
              다시 시도
            </Button>
          }
        />
      ) : (
        <DashboardBody data={dashboardQuery.data} onNavigate={navigate} />
      )}
    </>
  );
}

function DashboardBody({
  data,
  onNavigate,
}: {
  data: DashboardResponse;
  onNavigate: ReturnType<typeof useNavigate>;
}) {
  const { kpis, trend, directionSplit, actionQueue } = data;

  return (
    <div className="flex flex-col gap-6">
      {/* KPI 6개 */}
      <div className="grid grid-cols-3 gap-4 xl:grid-cols-6">
        <KpiCard
          label="GMV"
          value={formatMoney({ amount: kpis.gmv_krw, currency: 'KRW' })}
          sub={`정산 ${formatCount(kpis.settled_count, '건')}`}
        />
        <KpiCard
          label="거래 수"
          value={formatCount(kpis.orders_count, '건')}
          sub={`진행 요청 ${formatCount(kpis.active_requests, '건')}`}
        />
        <KpiCard
          label="신규 가입 (KR)"
          value={formatCount(kpis.signups_kr, '명')}
        />
        <KpiCard
          label="신규 가입 (JP)"
          value={formatCount(kpis.signups_jp, '명')}
        />
        <KpiCard label="채팅 수" value={formatCount(kpis.messages_count, '건')} />
        <KpiCard
          label="분쟁"
          value={formatCount(kpis.open_disputes, '건')}
          tone={kpis.open_disputes > 0 ? 'warning' : 'default'}
        />
      </div>

      {/* 조치 필요 큐 */}
      <div className="grid grid-cols-3 gap-4">
        <ActionQueueCard
          icon={<ShieldAlert className="size-5" aria-hidden="true" />}
          label="미결 분쟁"
          count={actionQueue.pendingDisputes}
          onClick={() => void onNavigate({ to: '/disputes' })}
        />
        <ActionQueueCard
          icon={<PackageX className="size-5" aria-hidden="true" />}
          label="지연 배송"
          count={actionQueue.delayedShipments}
          onClick={() => void onNavigate({ to: '/orders' })}
        />
        <ActionQueueCard
          icon={<AlertTriangle className="size-5" aria-hidden="true" />}
          label="열린 신고"
          count={actionQueue.openReports}
          onClick={() => void onNavigate({ to: '/disputes' })}
        />
      </div>

      {/* 추이 + 방향별 */}
      <div className="grid grid-cols-3 gap-6">
        <section className="col-span-2 rounded-lg border border-line bg-surface p-6">
          <h2 className="mb-4 text-title3 text-fg">거래 추이</h2>
          <TrendChart data={trend} />
        </section>
        <section className="rounded-lg border border-line bg-surface p-6">
          <h2 className="mb-4 text-title3 text-fg">방향별 비율</h2>
          <DirectionSplit data={directionSplit} />
        </section>
      </div>
    </div>
  );
}

function ActionQueueCard({
  icon,
  label,
  count,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  count: number;
  onClick: () => void;
}) {
  const urgent = count > 0;
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center justify-between gap-3 rounded-lg border border-line bg-surface p-5 text-left transition-colors hover:bg-subtle focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-600"
    >
      <span className="flex items-center gap-3">
        <span className={urgent ? 'text-warning-700' : 'text-fg-tertiary'}>{icon}</span>
        <span className="text-caption1 font-medium text-fg">{label}</span>
      </span>
      <span
        className={
          urgent
            ? 'text-title2 tabular-nums font-semibold text-warning-700'
            : 'text-title2 tabular-nums text-fg-tertiary'
        }
      >
        {count}
      </span>
    </button>
  );
}
